import { History } from "lucide-react";
import { useState } from "react";
import { Explainer } from "../components/Explainer";
import { Link } from "react-router-dom";
import { maintenance, type ComponentHistory } from "../lib/api";
import { documentOpenPath } from "./documentViewFlow";
import { TableScroll } from "../components/TableScroll";

/** Every visit that named the component, oldest first, each cited page linked to its rendering. */
export function HistoryTimeline({ history, pkg = null }: { history: ComponentHistory; pkg?: string | null }) {
  if (history.appearances.length === 0 && history.unavailable) {
    return (
      <p className="muted" role="status">
        The history of {history.component} could not be checked. {history.unavailable}
      </p>
    );
  }
  const unread = history.unread ?? 0;
  const partial = unread > 0 && (
    <p className="muted" role="status" style={{ marginBottom: 12 }}>
      {unread} of {history.indexed ?? unread} visit reports have not been read into the store yet, so this
      history may be incomplete.
    </p>
  );
  if (history.appearances.length === 0) {
    return partial || <p className="muted">No site visit report names {history.component}.</p>;
  }
  return (
    <>
      {partial}
      <p style={{ marginBottom: 12 }}>
        {history.component} is named in {history.reports} visit report{history.reports === 1 ? "" : "s"}
        {history.years.length > 0 && ` from ${history.years[0]} to ${history.years[history.years.length - 1]}`}.
        {history.recurring && <strong> Repeat attention across years.</strong>}
      </p>
      <section className="card" style={{ padding: 0, overflow: "hidden" }}>
        <TableScroll label="Site visits">
        <table>
          <thead><tr><th>Date</th><th>Visit</th><th>Unit</th><th>Author</th><th>Cited pages</th></tr></thead>
          <tbody>
            {history.appearances.map((a) => (
              <tr key={`${a.path}-${a.date}`}>
                <td className="mono">{a.date || "undated"}</td>
                <td>{a.title}</td>
                <td>{a.unit || "not recorded"}</td>
                <td>{a.author || "not recorded"}</td>
                <td>
                  {a.pages.map((p) => (
                    <Link key={p} to={documentOpenPath({ path: a.path, pkg, page: p }) ?? "/documents"}
                          className="mono" style={{ marginRight: 8 }} aria-label={`Open ${a.title || "the report"} at page ${p}`}>
                      p.{p}
                    </Link>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      </section>
    </>
  );
}

/** Ask for a component's site-visit history in a package. Shell's workflow area 4. */
export function MaintenanceHistory() {
  const [pkg, setPkg] = useState("GTG");
  const [component, setComponent] = useState("");
  const [history, setHistory] = useState<ComponentHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    if (!component.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setHistory(await maintenance.history(pkg.trim(), component.trim()));
    } catch (e) {
      setHistory(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>
        <span className="row"><History size={22} /> Maintenance History</span>
      </h2>
      <p className="muted" style={{ marginBottom: 24 }}>
        Every Siemens site visit that named a component, oldest first, with the pages that name it.
      </p>
      <form className="row" style={{ gap: 8, marginBottom: 20, flexWrap: "wrap" }}
            onSubmit={(e) => { e.preventDefault(); void ask(); }}>
        <input aria-label="Package" value={pkg} onChange={(e) => setPkg(e.target.value)} style={{ width: 120 }} />
        <input aria-label="Component" placeholder="for example combustion chamber" value={component}
               onChange={(e) => setComponent(e.target.value)} style={{ minWidth: 260 }} />
        <button type="submit" disabled={busy || !component.trim()}>{busy ? "Searching" : "Show history"}</button>
      </form>
      {error && <p className="muted" role="alert">{error}</p>}
      {history && <HistoryTimeline history={history} />}
      <div style={{ marginTop: 24 }}>
        <Explainer>
          Reports what was found and when. Seventy reports over fifteen years cannot support a failure
          statistic, so none is shown.
        </Explainer>
      </div>
    </div>
  );
}
